/**
 * SchedaIscritti — chi ha risposto (una riga per persona: la risposta attuale), coi contatti, la
 * provenienza, la lingua, il consenso e il «non contattare». Filtri per provenienza, consenso e
 * «domanda → opzione» (chi ha scelto Magazzino); esportazione in CSV di ciò che si vede.
 * Clic su una riga = scheda del lead. Dal 20/10/2026 anche «a che punto è» ogni iscritto: lo stato del percorso e
 * il prossimo passo, che il server ricava dal registro delle azioni (`PercorsoLead` nella scheda).
 */

import React, { useMemo, useState } from 'react';
import {
  Box, Button, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import { Download } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { contaNeiConti, prossimoPasso, TIPI_CON_OPZIONI } from '@shared/questionari';
import { tokens } from '../../../theme';
import { SoftChip } from '../../SoftChip';
import type { PlatformLead } from '../../../types/platformCrm';
import { CONSENSO_LABELS, CONSENSO_TONES, formatDate, formatDateTime, millisDi, nomeLingua, STATO_LABELS, STATO_TONES } from '../labels';
import type { DatiQuestionario } from './useDatiQuestionario';
import { csvIscritti, nomeFileCsv, scaricaCsv } from './esportaCsv';
import { contattiDi, provenienzaDi, statoConsenso } from './filtri';
import { numero } from './Grafici';
import { ElencoIscrittiCompatto } from './ElencoIscrittiCompatto';

const TUTTI = '__tutti__';
type FiltroConsenso = typeof TUTTI | 'nessuno' | 'da_confermare' | 'confermato' | 'revocato';

export const SchedaIscritti: React.FC<{
  dati: DatiQuestionario;
  onApriLead: (lead: PlatformLead) => void;
}> = ({ dati, onApriLead }) => {
  const { t } = useTranslation('crm');
  const [provenienza, setProvenienza] = useState<string>(TUTTI);
  const [consenso, setConsenso] = useState<FiltroConsenso>(TUTTI);
  const [domandaId, setDomandaId] = useState<string>('');
  const [opzioneId, setOpzioneId] = useState<string>('');
  const [cerca, setCerca] = useState('');
  // Sul telefono gli iscritti sono righe con email e telefono da toccare: la tabella è larga 1.100 px.
  const telefono = useMediaQuery(useTheme().breakpoints.down('md'));

  const domande = dati.normalizzato?.domande ?? [];
  const domandeAScelta = domande.filter((d) => TIPI_CON_OPZIONI.includes(d.tipo));
  const domandaScelta = domandeAScelta.find((d) => d.id === domandaId);

  const attuali = useMemo(
    () => dati.risposte
      .filter(contaNeiConti)
      .sort((a, b) => (millisDi(b.completato_at) ?? 0) - (millisDi(a.completato_at) ?? 0)),
    [dati.risposte],
  );
  const provenienzeNote = useMemo(() => Array.from(new Set(attuali.map(provenienzaDi))).sort((a, b) => a.localeCompare(b, 'it')), [attuali]);

  const filtrate = useMemo(() => {
    const testo = cerca.trim().toLowerCase();
    return attuali.filter((r) => {
      const l = dati.lead.get(r.platform_lead_id);
      if (provenienza !== TUTTI && provenienzaDi(r) !== provenienza) return false;
      if (consenso !== TUTTI && statoConsenso(l) !== consenso) return false;
      if (domandaScelta && opzioneId) {
        const riga = (r.risposte ?? []).find((x) => x.domanda_id === domandaScelta.id);
        const scelte = riga ? (Array.isArray(riga.valore) ? riga.valore : [String(riga.valore)]) : [];
        if (!scelte.includes(opzioneId)) return false;
      }
      if (testo) {
        const c = contattiDi(r, l);
        if (![c.nome, c.azienda, c.email, c.telefono, c.ruolo].some((v) => v.toLowerCase().includes(testo))) return false;
      }
      return true;
    });
  }, [attuali, dati.lead, provenienza, consenso, domandaScelta, opzioneId, cerca]);

  const esporta = () => {
    const titolo = dati.questionario?.titolo ?? 'questionario';
    scaricaCsv(csvIscritti(domande, filtrate, dati.lead), nomeFileCsv(titolo));
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ flexWrap: 'wrap', rowGap: 1.5 }}>
        <TextField size="small" label={t('iscritti.cerca')} placeholder={t('iscritti.cerca_esempio')} value={cerca} onChange={(e) => setCerca(e.target.value)} sx={{ minWidth: 220 }} />
        <TextField select size="small" label={t('iscritti.provenienza')} value={provenienza} onChange={(e) => setProvenienza(e.target.value)} sx={{ minWidth: 170 }}>
          <MenuItem value={TUTTI}>{t('iscritti.tutte')}</MenuItem>
          {provenienzeNote.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
        </TextField>
        <TextField select size="small" label={t('iscritti.consenso')} value={consenso} onChange={(e) => setConsenso(e.target.value as FiltroConsenso)} sx={{ minWidth: 170 }}>
          <MenuItem value={TUTTI}>{t('iscritti.tutti')}</MenuItem>
          {(['confermato', 'da_confermare', 'revocato', 'nessuno'] as const).map((s) => <MenuItem key={s} value={s}>{CONSENSO_LABELS[s]}</MenuItem>)}
        </TextField>
        <TextField
          select
          size="small"
          label={t('iscritti.domanda')}
          value={domandaId}
          onChange={(e) => { setDomandaId(e.target.value); setOpzioneId(''); }}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">{t('iscritti.nessun_filtro')}</MenuItem>
          {domandeAScelta.map((d) => <MenuItem key={d.id} value={d.id}>{d.nome_breve || d.testo}</MenuItem>)}
        </TextField>
        {domandaScelta && (
          <TextField select size="small" label={t('iscritti.ha_scelto')} value={opzioneId} onChange={(e) => setOpzioneId(e.target.value)} sx={{ minWidth: 200 }}>
            <MenuItem value="">{t('iscritti.qualsiasi')}</MenuItem>
            {(domandaScelta.opzioni ?? []).map((o) => <MenuItem key={o.id} value={o.id}>{o.etichetta}</MenuItem>)}
          </TextField>
        )}
        <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />
        <Button variant="outlined" startIcon={<Download />} onClick={esporta} disabled={filtrate.length === 0}>
          {t('iscritti.esporta', { n: numero(filtrate.length) })}
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ borderColor: tokens.color.line, overflowX: 'auto' }}>
        {telefono ? (
          <ElencoIscrittiCompatto
            iscritti={filtrate}
            lead={dati.lead}
            vuoto={attuali.length === 0 ? t('iscritti.nessun_iscritto') : t('iscritti.nessuno_coi_filtri')}
            onApriLead={onApriLead}
          />
        ) : (
          <Table size="small" sx={{ minWidth: 1260, '& tr:last-child td': { borderBottom: 0 } }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('iscritti.colonne.data')}</TableCell>
                <TableCell>{t('iscritti.colonne.nome')}</TableCell>
                <TableCell>{t('iscritti.colonne.ruolo')}</TableCell>
                <TableCell>{t('iscritti.colonne.azienda')}</TableCell>
                <TableCell>{t('iscritti.colonne.a_che_punto')}</TableCell>
                <TableCell>{t('iscritti.colonne.email')}</TableCell>
                <TableCell>{t('iscritti.colonne.telefono')}</TableCell>
                <TableCell>{t('iscritti.colonne.provenienza')}</TableCell>
                <TableCell>{t('iscritti.colonne.lingua')}</TableCell>
                <TableCell>{t('iscritti.colonne.consenso')}</TableCell>
                <TableCell>{t('iscritti.colonne.non_contattare')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtrate.map((r) => {
                const l = dati.lead.get(r.platform_lead_id);
                const c = contattiDi(r, l);
                const stato = statoConsenso(l);
                return (
                  <TableRow
                    key={r.id}
                    hover
                    onClick={() => l && onApriLead(l)}
                    sx={{ cursor: l ? 'pointer' : 'default' }}
                    title={l ? t('iscritti.apri_scheda') : t('iscritti.scheda_non_leggibile')}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap', color: tokens.color.ash }}>{formatDateTime(r.completato_at)}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{c.nome || '—'}</TableCell>
                    <TableCell>{c.ruolo || '—'}</TableCell>
                    <TableCell>{c.azienda || '—'}</TableCell>
                    <TableCell sx={{ minWidth: 170 }}>
                      {l ? (
                        <>
                          <SoftChip label={STATO_LABELS[l.stato]} tone={STATO_TONES[l.stato]} />
                          <Typography variant="caption" sx={{ display: 'block', color: tokens.color.ash, mt: 0.5, lineHeight: 1.3 }}>
                            {t(`percorso.passo.${l.prossimo_passo ?? prossimoPasso(l.stato, l.registro_ultima?.tipo ?? null)}`)}
                          </Typography>
                        </>
                      ) : '—'}
                    </TableCell>
                    <TableCell sx={{ overflowWrap: 'anywhere' }}>{c.email || '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{c.telefono || '—'}</TableCell>
                    <TableCell>{provenienzaDi(r)}</TableCell>
                    <TableCell>{r.lingua ? nomeLingua(r.lingua) : '—'}</TableCell>
                    <TableCell><SoftChip label={CONSENSO_LABELS[stato]} tone={CONSENSO_TONES[stato]} /></TableCell>
                    <TableCell>
                      {l?.non_contattare ? <SoftChip label={t('iscritti.non_contattare_dal', { data: formatDate(l.non_contattare_at) })} tone="error" /> : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtrate.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 5 }}>
                    <Typography variant="body2" sx={{ color: tokens.color.mist }}>
                      {attuali.length === 0 ? t('iscritti.nessun_iscritto') : t('iscritti.nessuno_coi_filtri')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
};
